import type { UseMutationResult } from "@tanstack/react-query";

import { useMemo } from "react";
// Removed imports for toast and useTranslation as we now handle notifications at the component level

// Import types from API declarations
import type { ApiResponse } from "./types";

// Import mutations with type assertions
import {
  useCreateSong,
  useUpdateSong,
} from "../../../../../api/mutations";
// Import helpers
// No need to import strToBool as we implement equivalent functionality inline

// Types for the hook parameters and return values
export type WriterSplit = {
  writercode: string;
  writershare: number;
  writerdesignationcode: string;
  righttocollect: boolean;
  writersplitamendmentidentifier?: string;
  writersplitidentifier?: string;
  // Additional writer fields
  firstname?: string | null;
  lastname?: string | null;
  middlename?: string | null;
  writersociety?: string | null;
  dateofbirth?: string | null;
};

export type PerformingArtist = {
  name: string;
  performingartistidentifier?: string;
  performingartistamendmentidentifier?: string;
};

export type AlternativeTitle = {
  title: string;
};

export type CreateSongData = {
  artistaccountcode?: string; // Added per Swagger model
  title: string;
  clientid?: string;
  iswc?: string;
  durationmins: number;
  durationsecs: number;
  isremix: boolean;
  containssamples: boolean;
  ispublicdomain?: boolean;
  alternativetitles?: AlternativeTitle[];
  performingartistnames?: PerformingArtist[];
  writersplits?: WriterSplit[];
  recordings?: Array<{
    artistaccountcode: string; // Required field per Swagger
    workcode?: string;
    title: string;
    performingartist: string;
    isrc?: string;
    recordlabel?: string;
    uicbarcode?: string;
    releasedate?: string;
    spotifyidentifier?: string;
    spotifypopularity?: number;
    spotifypopularitylastupdatedutc?: string;
    explicit?: boolean;
    tracknumber?: number;
    discnumber?: number;
  }>;
};

export type UpdateSongData = {
  workcode: string;
  etag: string;
  iswc?: string;
  recordings?: unknown[]; // Using unknown instead of any
} & CreateSongData;

type SongOperationsOptions = {
  selectedAccount: string;
  onSuccess?: (data: ApiResponse) => void;
  onValidationError?: (messages: string[]) => void;
  onError?: (message: string) => void;
};

type SongOperationsResult = {
  createSong: (songData: CreateSongData) => Promise<ApiResponse>;
  updateSong: (songData: UpdateSongData) => Promise<ApiResponse>;
  isCreating: boolean;
  isUpdating: boolean;
  isLoading: boolean;
};

// Define mutation hooks as proper types with generics
type CreateSongMutationHook = UseMutationResult<
  ApiResponse,
  Error,
  Record<string, unknown>,
  unknown
>;

type UpdateSongMutationHook = UseMutationResult<
  ApiResponse,
  Error,
  Record<string, unknown>,
  unknown
>;

/**
 * Custom hook for managing song operations like create and update
 */
export function useSongOperations({
  selectedAccount,
  onSuccess = () => {},
  onValidationError = () => {},
  onError = () => {},
}: SongOperationsOptions): SongOperationsResult {
  // Removed useTranslation as we don't need it anymore for toasts
  // Cast the mutation hooks to their proper types
  // Using type assertion here since we know the structure matches
  const createMutation = useCreateSong() as unknown as CreateSongMutationHook;
  const updateMutation = useUpdateSong() as unknown as UpdateSongMutationHook;

  const operations = useMemo(() => ({
    /**
     * Create a new song
     */
    createSong: async (songData: CreateSongData): Promise<ApiResponse> => {
      // Only reset if it exists
      // Reset the mutation state if possible
      if (typeof createMutation.reset === "function") {
        createMutation.reset();
      }

      // Format data for API
      const {
        title,
        durationmins,
        durationsecs,
        isremix,
        containssamples,
        alternativetitles = [],
        performingartistnames = [],
        writersplits = [],
        recordings = [], // Optional recordings field
      } = songData;

      // Filter out empty alternative titles
      const filteredAlts = alternativetitles.filter(a => Boolean(a.title) && a.title.length > 0);

      const data = {
        artistaccountcode: selectedAccount,
        title,
        durationmins,
        durationsecs,
        isremix,
        containssamples,
        ispublicdomain: true, // Default value
        alternativetitles: filteredAlts,
        performingartistnames,
        writersplits: writersplits.map((w) => {
          return {
            ...w,
            righttocollect: w.righttocollect,
          };
        }),
        // Include recordings only if they are provided (optional)
        ...(recordings.length > 0 && { recordings }),
      };

      return new Promise<ApiResponse>((resolve, reject) => {
        createMutation.mutate(data, {
          onSuccess: (response: ApiResponse) => {
            if (response?.failedvalidation) {
              onValidationError(response.messages ?? []);
              reject(new Error("Validation failed"));
            }
            else {
              // Removed toast notification as it's now handled at the component level
              onSuccess(response);
              resolve(response);
            }
          },
          onError: (error: Error) => {
            onError(error?.message || "Unknown error");
            reject(error);
          },
        });
      });
    },

    /**
     * Update an existing song
     */
    updateSong: async (songData: UpdateSongData): Promise<ApiResponse> => {
      // Only reset if it exists
      // Reset the mutation state if possible
      if (typeof updateMutation.reset === "function") {
        updateMutation.reset();
      }

      const {
        title,
        durationmins,
        durationsecs,
        isremix,
        containssamples,
        alternativetitles = [],
        performingartistnames = [],
        writersplits = [],
        workcode,
        etag,
        iswc,
        recordings,
      } = songData;

      // Filter out empty alternative titles
      const filteredAlts = alternativetitles.filter(a => Boolean(a.title) && a.title.length > 0);

      // Format performers data for API
      const performersFormatted = performingartistnames.map(a => ({
        performingartistname: a.name,
        performingartistidentifier: Object.prototype.hasOwnProperty.call(a, "performingartistidentifier")
          ? a.performingartistidentifier
          : "00000000-0000-0000-0000-000000000000",
        performingartistamendmentidentifier: Object.prototype.hasOwnProperty.call(a, "performingartistamendmentidentifier")
          ? a.performingartistamendmentidentifier
          : "00000000-0000-0000-0000-000000000000",
      }));

      // Format writer splits data for API
      const writersplitsFormatted = writersplits.map((a) => {
        return {
          writercode: a.writercode,
          writershare: a.writershare,
          writerdesignationcode: a.writerdesignationcode,
          righttocollect: a.righttocollect,
          writersplitamendmentidentifier: Object.prototype.hasOwnProperty.call(a, "writersplitamendmentidentifier")
            ? a.writersplitamendmentidentifier
            : "00000000-0000-0000-0000-000000000000",
          writersplitidentifier: Object.prototype.hasOwnProperty.call(a, "writersplitidentifier")
            ? a.writersplitidentifier
            : "00000000-0000-0000-0000-000000000000",
          // Include additional writer fields if provided
          ...(a.firstname !== undefined && { firstname: a.firstname }),
          ...(a.lastname !== undefined && { lastname: a.lastname }),
          ...(a.middlename !== undefined && { middlename: a.middlename }),
          ...(a.writersociety !== undefined && { writersociety: a.writersociety }),
          ...(a.dateofbirth !== undefined && { dateofbirth: a.dateofbirth }),
        };
      });

      const data = {
        artistaccountcode: selectedAccount,
        title,
        durationmins,
        durationsecs,
        isremix,
        containssamples,
        ispublicdomain: true,
        alternativetitles: filteredAlts,
        performingartistnames: performersFormatted,
        writersplits: writersplitsFormatted,
        workcode,
        etag,
        iswc,
        recordings,
      };

      return new Promise<ApiResponse>((resolve, reject) => {
        updateMutation.mutate(data, {
          onSuccess: (response: ApiResponse) => {
            if (response?.failedvalidation) {
              onValidationError(response.messages ?? []);
              reject(new Error("Validation failed"));
            }
            else {
              // Removed toast notification as it's now handled at the component level
              onSuccess(response);
              resolve(response);
            }
          },
          onError: (error: Error) => {
            onError(error?.message || "Unknown error");
            reject(error);
          },
        });
      });
    },
  }), [createMutation, updateMutation, selectedAccount, onSuccess, onValidationError, onError]);

  // Return operations and loading states
  const isCreating = Boolean(createMutation?.isLoading);
  const isUpdating = Boolean(updateMutation?.isLoading);
  const isLoading = isCreating || isUpdating;

  return {
    ...operations,
    isCreating,
    isUpdating,
    isLoading,
  };
}
